import React, { useEffect, useState, useMemo } from "react";
import { api, formatErr } from "@/lib/api";
import { Wallet, Coins } from "lucide-react";

/**
 * CashBoxPicker — reusable component that lets user pick BOTH:
 *   1. Currency (from company's enabled currencies + all system currencies)
 *   2. Cash box (auto-filtered to the chosen currency; auto-created backend-side if missing)
 *
 * Props:
 *   value: { currency, cash_box_id }
 *   onChange: (patch) => void  — receives {currency?, cash_box_id?}
 *   company: { base_currency, enabled_currencies } — from useAuth().currentCompany
 *   label?: string
 *   compact?: boolean — smaller layout
 *   showLabel?: boolean — default true
 *   data-testid?: string
 */
export default function CashBoxPicker({ value = {}, onChange, company, label = "الصندوق والعملة", compact = false, showLabel = true, "data-testid": testId }) {
  const [boxes, setBoxes] = useState([]);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        api.get("/cashboxes").catch(() => ({ data: [] })),
        api.get("/currencies").catch(() => ({ data: { rates: {} } })),
      ]);
      setBoxes(b.data || []);
      setRates((r.data && r.data.rates) || {});
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Currencies allowed for this company:
  //   base_currency + enabled_currencies + any that already have a box + all system rates
  const availableCurrencies = useMemo(() => {
    const set = new Set();
    if (company?.base_currency) set.add(company.base_currency);
    (company?.enabled_currencies || []).forEach(c => set.add(c));
    boxes.forEach(b => set.add(b.currency));
    Object.keys(rates).forEach(c => set.add(c));
    return Array.from(set).sort();
  }, [company, boxes, rates]);

  const currency = value.currency || company?.base_currency || "SYP";
  const boxesForCurrency = boxes.filter(b => b.currency === currency);
  const currentBox = boxes.find(b => b.id === value.cash_box_id) || boxesForCurrency[0];

  const pickCurrency = (c) => {
    // switch currency AND auto-select first box in that currency (if any)
    const first = boxes.find(b => b.currency === c);
    onChange && onChange({ currency: c, cash_box_id: first ? first.id : null });
  };
  const pickBox = (bid) => onChange && onChange({ cash_box_id: bid });

  if (loading) return <div className="text-xs text-muted-foreground p-2">جاري التحميل...</div>;

  return (
    <div className={compact ? "space-y-1" : "space-y-2"} data-testid={testId || "cashbox-picker"}>
      {showLabel && <div className="text-xs font-medium flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> {label}</div>}
      <div className={compact ? "grid grid-cols-2 gap-1.5" : "grid grid-cols-1 sm:grid-cols-2 gap-2"}>
        {/* Currency */}
        <div>
          <select
            value={currency}
            onChange={(e) => pickCurrency(e.target.value)}
            className={`w-full border rounded bg-background text-sm ${compact ? "h-8 px-1.5" : "h-10 px-2"}`}
            data-testid={`${testId || "cashbox"}-currency`}
          >
            {availableCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {!compact && <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1"><Coins className="w-2.5 h-2.5" /> {availableCurrencies.length} عملة متاحة</div>}
        </div>
        {/* Box */}
        <div>
          {boxesForCurrency.length === 0 ? (
            <div className={`text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded flex items-center ${compact ? "h-8 px-2" : "h-10 px-2"}`}>
              سيُنشأ صندوق تلقائياً
            </div>
          ) : (
            <select
              value={value.cash_box_id || currentBox?.id || ""}
              onChange={(e) => pickBox(e.target.value)}
              className={`w-full border rounded bg-background text-sm ${compact ? "h-8 px-1.5" : "h-10 px-2"}`}
              data-testid={`${testId || "cashbox"}-box`}
            >
              {boxesForCurrency.map(b => (
                <option key={b.id} value={b.id}>
                  {`${b.name || `صندوق ${b.currency}`} — رصيد: ${(b.balance || 0).toLocaleString("ar-EG")}`}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
}
